import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useCore } from '@phoenix/core/frontend/shared/feature/context/core.context';
import { getLocale } from '@phoenix/core/frontend/shared/util/locales/locales';
import { ChipTabs, ChipTabContentType } from '@phoenix/design-system/frontend/ui/chip-tabs';

import { useGetVideosQuery } from '@phoenix/team-m/dashboard/frontend/domain/generated/graphql';
import { VideosChannel } from '@phoenix/team-m/dashboard/frontend/ui/interesting-for-you/videos-channel';
import { useQueryContextMemo } from '@phoenix/team-m/dashboard/frontend/shared/use-query-context-memo';
import { mapToVideoChannel } from '@phoenix/team-m/dashboard/frontend/util/video-channel/video-channel.mapper';
import { OverviewSubsection } from '@phoenix/team-m/dashboard/frontend/ui/overview/overview-subsection';
import { VideoChannel } from '@phoenix/team-m/dashboard/frontend/util/video-channel/video-channel.model';

export const YoutubeVideos: FC = () => {
  const { t } = useTranslation();
  const { 'videos-channels': flagEnabled } = useFlags();
  const {
    auth: { user },
  } = useCore();

  const stihlId = user?.stihl_id || '';

  const [{ data, fetching, error }] = useGetVideosQuery({
    variables: {
      locale: getLocale().RFC5646,
    },
    pause: !flagEnabled || stihlId === '',
    context: useQueryContextMemo(),
  });

  const latestVideosChannel = data?.videos?.youtube?.latest ? mapToVideoChannel(data.videos.youtube.latest) : undefined;
  const doItYourselfChannel = data?.videos?.youtube?.doItYourself ? mapToVideoChannel(data.videos.youtube.doItYourself) : undefined;
  const gardeningChannel = data?.videos?.youtube?.gardening ? mapToVideoChannel(data.videos.youtube.gardening) : undefined;
  const professionalsChannel = data?.videos?.youtube?.forProfessionals
    ? mapToVideoChannel(data.videos.youtube.forProfessionals)
    : undefined;

  const mapChannelToContent = (label: string, id: string, channel: VideoChannel) => ({
    label,
    id,
    view: <VideosChannel channel={channel} title={label} dataTestId={id} isLoading={(fetching || !data) && !error} />,
  });

  const content: ChipTabContentType[] = [
    ...(latestVideosChannel ? [mapChannelToContent(t('dashboard_youtube_tab_latest'), 'videoChannel_latest', latestVideosChannel)] : []),
    ...(doItYourselfChannel ? [mapChannelToContent(t('dashboard_youtube_tab_diy'), 'videoChannel_diy', doItYourselfChannel)] : []),
    ...(gardeningChannel ? [mapChannelToContent(t('dashboard_youtube_tab_gardening'), 'videoChannel_gardening', gardeningChannel)] : []),
    ...(professionalsChannel
      ? [mapChannelToContent(t('dashboard_youtube_tab_professionals'), 'videoChannel_professionals', professionalsChannel)]
      : []),
  ];

  return flagEnabled && content.length ? (
    <OverviewSubsection
      className="relative"
      headline={t('dashboard_youtube_headline')}
      subHeadline={t('dashboard_youtube_subHeadline')}
      dataTestId="videosSection"
    >
      {content.length === 1 ? (
        content[0].view
      ) : (
        <ChipTabs className="relative mt-6 desktop:mt-8" filterLabel={t('dashboard_youtube_filter_label')} content={content} />
      )}
    </OverviewSubsection>
  ) : null;
};
